import type {
  AskInput,
  AskResult,
  AuthResponse,
  ChildProfile,
  DeletionResult,
  CompleteCheckInput,
  CompleteCheckResult,
  GoalSuggestion,
  GoalsResponse,
  LoginChildInput,
  ParentDigest,
  ParentSessionResponse,
  RegisterChildInput,
  ShellDescriptor,
  SkyResponse,
  StarDetailResponse,
  Turn,
} from "@howcomet/shared";

const BASE_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8787";

const CHILD_TOKEN_KEY = "howcomet.child.token";
const HANDLE_KEY = "howcomet.child.handle";

/**
 * The one place that talks to the service. Two things it deliberately does not
 * do: no third-party SDK is imported here, and no analytics call is made from
 * the kid surface — under-13 means COPPA, read strictly.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/*
  One 403 the whole app has to agree about.

  `needs_grown_up` is not this screen's problem — it is the family's, and every
  gated route returns it at once. Left to the screens, each tab caught it
  separately and rendered it as its own load error, so a lapsed subscription
  looked like three unrelated failures depending on which tab happened to be
  open. This hoists it: the client still decides nothing about entitlement, it
  just notices that the server said so and tells the one component that can act.

  Deliberately not limited to `/api/me`, which is gated by `requireChild` alone
  and so never returns this — the answer only ever arrives on the way to
  somewhere else.
*/
let needsGrownUp: ((message: string) => void) | null = null;

export function onNeedsGrownUp(fn: (message: string) => void): void {
  needsGrownUp = fn;
}

export function getChildToken(): string | null {
  return localStorage.getItem(CHILD_TOKEN_KEY);
}

export function setChildToken(token: string): void {
  localStorage.setItem(CHILD_TOKEN_KEY, token);
}

export function clearChildToken(): void {
  localStorage.removeItem(CHILD_TOKEN_KEY);
}

/** Remembered so a kid doesn't have to retype a handle they can barely read. */
export function getRememberedHandle(): string {
  return localStorage.getItem(HANDLE_KEY) ?? "";
}

export function rememberHandle(handle: string): void {
  localStorage.setItem(HANDLE_KEY, handle);
}

/**
 * How long a child is ever asked to wait before being told something.
 *
 * A question takes about eleven seconds, and a slow one about twenty. Past
 * forty, whatever is happening is not going to end well, and the tablet's own
 * default — sixty seconds of nothing — is indistinguishable from the app being
 * broken. That is exactly how it was described: "stuck in infinity loading".
 */
const TIMEOUT_MS = 40_000;

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; token?: string | null } = {},
): Promise<T> {
  const token = options.token !== undefined ? options.token : getChildToken();
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: options.method ?? "GET",
      headers: {
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: abort.signal,
    });
  } catch {
    /*
      Two different failures, and telling them apart matters: "check the wifi"
      sends a child to look at a router when the truth is that we were slow.
    */
    throw abort.signal.aborted
      ? new ApiError(0, "timeout", "That one is taking too long. Try asking again.")
      : new ApiError(0, "offline", "I can't reach the stars right now. Check the wifi.");
  } finally {
    clearTimeout(timer);
  }

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => ({}))) as {
    error?: string;
    message?: string;
  };

  if (!response.ok) {
    /*
      The server's wording is trusted for a 4xx — those are refusals we wrote,
      like "one at a time" or "sign in again". A 5xx is not: it is the shape of
      failure nobody planned, which is when a raw provider message once reached
      a child's screen as their answer. The last line of defence belongs on this
      side too, because the service that leaks it is the service that is already
      having a bad day.
    */
    const trusted = response.status >= 400 && response.status < 500 && payload.message;

    // Before the throw, so it fires whether or not the caller catches this one.
    if (response.status === 403 && payload.error === "needs_grown_up" && trusted) {
      needsGrownUp?.(payload.message!);
    }

    throw new ApiError(
      response.status,
      payload.error ?? "error",
      trusted ? payload.message! : "Something went wrong here. Try again in a moment.",
    );
  }
  return payload as T;
}

export const api = {
  register: (input: RegisterChildInput) =>
    request<AuthResponse>("/api/children", { method: "POST", body: input, token: null }),

  login: (input: LoginChildInput) =>
    request<AuthResponse>("/api/children/login", { method: "POST", body: input, token: null }),

  me: () => request<ChildProfile>("/api/me"),

  suggestions: () => request<{ suggestions: string[] }>("/api/suggestions"),

  shells: () => request<{ shells: ShellDescriptor[] }>("/api/shells", { token: null }),

  ask: (input: AskInput) => request<AskResult>("/api/ask", { method: "POST", body: input }),

  /** The exploration in progress, so a reload doesn't lose the conversation. */
  thread: () => request<{ threadId: string | null; turns: Turn[] }>("/api/thread"),

  /** "I want to explore something new." Closes every open thread for this child. */
  guideReset: () => request<{ ok: boolean }>("/api/me/guide-reset", { method: "POST", body: {} }),
  guideSeen: () => request<{ ok: boolean }>("/api/me/guide-seen", { method: "POST", body: {} }),
  celebrated: (requestId: string) =>
    request<{ ok: boolean }>(`/api/goals/celebrated/${requestId}`, { method: "POST", body: {} }),
  archiveStar: (topicId: string) =>
    request<{ ok: boolean }>(`/api/sky/${topicId}/archive`, { method: "POST", body: {} }),
  closeThread: () => request<{ ok: boolean }>("/api/thread/close", { method: "POST", body: {} }),

  completeCheck: (attemptId: string, input: CompleteCheckInput) =>
    request<CompleteCheckResult>(`/api/checks/${attemptId}/complete`, {
      method: "POST",
      body: input,
    }),

  sky: () => request<SkyResponse>("/api/sky"),

  star: (topicId: string) => request<StarDetailResponse>(`/api/sky/${topicId}`),

  goals: () => request<GoalsResponse>("/api/goals"),

  setGoal: (rewardId: string) =>
    request<{ activeRewardId: string }>("/api/goals/active", {
      method: "POST",
      body: { rewardId },
    }),

  requestGoal: (rewardId: string) =>
    request<{ id: string }>("/api/goals/request", { method: "POST", body: { rewardId } }),

  suggestGoal: (text: string) =>
    request<GoalSuggestion>("/api/goals/suggest", { method: "POST", body: { text } }),

  /** The child says the thing turned up. Only they can say this. */
  goalArrived: (requestId: string, note?: string) =>
    request<{ ok: boolean }>(`/api/goals/requests/${requestId}/arrived`, {
      method: "POST",
      body: note ? { note } : {},
    }),

  /** It never came — take the stardust back and cancel the promise. */
  goalReturned: (requestId: string) =>
    request<{ ok: boolean; returned: number }>(`/api/goals/requests/${requestId}/returned`, {
      method: "POST",
      body: {},
    }),

  decideSuggestion: (
    parentToken: string,
    id: string,
    body: { decision: "added" | "declined"; cost?: number },
  ) =>
    request<{ status: string }>(`/api/parent/suggestions/${id}`, {
      method: "POST",
      body,
      token: parentToken,
    }),

  /** Resend the parent invite, or correct a mistyped address. */
  setParentEmail: (parentEmail?: string) =>
    request<{ parentEmail: string; parentReady: boolean }>("/api/children/parent-email", {
      method: "POST",
      body: parentEmail ? { parentEmail } : {},
    }),

  /* --- the parent portal, on its own URL ---------------------------- */

  parentSetupCheck: (token: string) =>
    request<{ email: string }>("/api/parent/setup/check", {
      method: "POST",
      body: { token },
      token: null,
    }),

  parentSetup: (token: string, password: string) =>
    request<ParentSessionResponse>("/api/parent/setup", {
      method: "POST",
      body: { token, password },
      token: null,
    }),

  parentLogin: (email: string, password: string) =>
    request<ParentSessionResponse>("/api/parent/login", {
      method: "POST",
      body: { email, password },
      token: null,
    }),

  parentForgot: (email: string) =>
    request<{ ok: boolean }>("/api/parent/forgot", {
      method: "POST",
      body: { email },
      token: null,
    }),

  parentSession: (parentToken: string) =>
    request<ParentSessionResponse>("/api/parent/session", { token: parentToken }),

  /** Unlocks the tablet's small grown-ups screen. Returns no parent token. */
  deviceUnlock: (password: string) =>
    request<{ ok: boolean }>("/api/device/unlock", { method: "POST", body: { password } }),

  parentDigest: (parentToken: string, childId?: string) =>
    request<ParentDigest>(`/api/parent/digest${childId ? `?childId=${childId}` : ""}`, {
      token: parentToken,
    }),

  decideRequest: (parentToken: string, id: string, decision: "approved" | "denied") =>
    request<{ status: string }>(`/api/parent/requests/${id}`, {
      method: "POST",
      body: { decision },
      token: parentToken,
    }),

  /** Puts a refused question away once the grown-up has talked about it. */
  parentDismissFlag: (parentToken: string, id: string) =>
    request<{ ok: boolean }>(`/api/parent/flagged/${id}/dismiss`, {
      method: "POST",
      body: {},
      token: parentToken,
    }),

  /**
   * Erase one child, or the whole family.
   *
   * The password goes back over the wire even though we hold a token, because
   * the server insists — a thirty-minute session is right for approving a
   * reward and far too loose for the one thing here that cannot be undone.
   */
  deleteChild: (parentToken: string, childId: string, password: string, confirm: string) =>
    request<DeletionResult>(`/api/parent/children/${childId}/delete`, {
      method: "POST",
      body: { password, confirm },
      token: parentToken,
    }),

  deleteAccount: (parentToken: string, password: string, confirm: string) =>
    request<DeletionResult>("/api/parent/account/delete", {
      method: "POST",
      body: { password, confirm },
      token: parentToken,
    }),

  /* --- the subscription, all behind the parent's password ----------- */

  parentSubscription: (parentToken: string) =>
    request<{
      productId: string; price: string; detail: string;
      status: string; source: string; entitledUntil: string | null; summary: string;
    }>("/api/parent/subscription", { token: parentToken }),

  /**
   * Hands Apple's signed transaction to the server, which is the only thing
   * that decides entitlement. The device reports; it never grants.
   */
  /**
   * What the tablet's grown-ups screen shows. Child token; price, status, and
   * the opaque account token handed to StoreKit at purchase. Nothing about the
   * adult.
   */
  subscription: () =>
    request<{
      productId: string; price: string; detail: string;
      status: string; entitledUntil: string | null; summary: string;
      accountToken: string;
    }>("/api/subscription"),

  reportPurchase: (signedTransaction: string) =>
    request<{ entitledUntil: string | null; status: string }>("/api/subscription/report", {
      method: "POST",
      body: { signedTransaction },
    }),

  upsertReward: (
    parentToken: string,
    reward: { id?: string; label: string; cost: number; emoji?: string },
  ) => request<unknown>("/api/parent/rewards", { method: "PUT", body: reward, token: parentToken }),

  deleteReward: (parentToken: string, id: string) =>
    request<void>(`/api/parent/rewards/${id}`, { method: "DELETE", token: parentToken }),

};
